// SM-2 spaced repetition: the exact schedule the iOS and Android apps use
// (pinned by shared/fixtures/sm2.json).

export type Grade = "again" | "hard" | "good" | "easy";
export const GRADES: { id: Grade; q: number; label: string; key: string }[] = [
  { id: "again", q: 0, label: "Again", key: "1" },
  { id: "hard", q: 3, label: "Hard", key: "2" },
  { id: "good", q: 4, label: "Good", key: "3" },
  { id: "easy", q: 5, label: "Easy", key: "4" },
];

export interface VocabCard {
  fa: string;
  finglish: string;
  en: string;
  note?: string | null;
  source: string; // chat | voice | story | lesson | shop | gloss | pronounce
  createdAt: number;
  ease: number;
  interval: number;
  repetitions: number;
  due: number;
  /** Sync bookkeeping: last local change; deleted cards stay as tombstones. */
  updatedAt: number;
  deleted?: boolean;
}

export function newCard(w: { fa: string; finglish: string; en: string; note?: string | null }, source: string, now = Date.now()): VocabCard {
  return { fa: w.fa.trim(), finglish: w.finglish, en: w.en, note: w.note ?? null, source, createdAt: now, ease: 2.5, interval: 0, repetitions: 0, due: now, updatedAt: now };
}

export function review(card: VocabCard, grade: Grade, now = Date.now()): VocabCard {
  const q = GRADES.find((g) => g.id === grade)!.q;
  let reps = card.repetitions;
  let iv = card.interval;
  let due: number;
  if (grade === "again") {
    reps = 0;
    iv = 0;
    due = now + 60_000;
  } else {
    reps += 1;
    iv = reps === 1 ? (grade === "easy" ? 3 : 1) : reps === 2 ? (grade === "easy" ? 7 : 4) : Math.round(iv * card.ease * (grade === "hard" ? 0.8 : 1));
    const d = new Date(now);
    d.setDate(d.getDate() + Math.max(1, iv));
    due = d.getTime();
  }
  const ease = Math.max(1.3, card.ease + (0.1 - (5 - q) * (0.08 + (5 - q) * 0.02)));
  return { ...card, ease, interval: iv, repetitions: reps, due, updatedAt: now };
}

export const isDue = (c: VocabCard, now = Date.now()) => !c.deleted && c.due <= now;
export const isMastered = (c: VocabCard) => c.interval >= 21;

/** Words the learner knows, sent to the tutor so new material stays ~80% familiar. */
export function knownWords(cards: VocabCard[]): string[] {
  return cards
    .filter((c) => !c.deleted && (c.repetitions >= 1 || isMastered(c)))
    .sort((a, b) => b.interval - a.interval)
    .slice(0, 200)
    .map((c) => c.fa);
}
